import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { LogoHome } from '../components/LogoHome';
import {
  ArrowLeft, Download, Calendar as CalendarIcon, DollarSign, Clock, Users2,
  Loader2, Check, RotateCcw, FileText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function periodPresets() {
  // Anchor everything to Australia/Sydney using local date math (browser's local tz is close enough for UI presets)
  const today = new Date();
  const dow = today.getDay(); // Sun=0..Sat=6
  const daysToMonday = (dow + 6) % 7;
  const monday = new Date(today); monday.setDate(today.getDate() - daysToMonday);
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const lastMonday = new Date(monday); lastMonday.setDate(monday.getDate() - 7);
  const lastSunday = new Date(sunday); lastSunday.setDate(sunday.getDate() - 7);
  const fnStart = new Date(monday); fnStart.setDate(monday.getDate() - 7);
  const lastFnStart = new Date(fnStart); lastFnStart.setDate(fnStart.getDate() - 14);
  const lastFnEnd = new Date(fnStart); lastFnEnd.setDate(fnStart.getDate() - 1);
  return {
    thisWeek: { start: toYMD(monday), end: toYMD(sunday), label: 'This week' },
    lastWeek: { start: toYMD(lastMonday), end: toYMD(lastSunday), label: 'Last week' },
    thisFortnight: { start: toYMD(fnStart), end: toYMD(sunday), label: 'This fortnight' },
    lastFortnight: { start: toYMD(lastFnStart), end: toYMD(lastFnEnd), label: 'Last fortnight' },
  };
}

function money(n) {
  return `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function EditableRateCell({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(row.rate));
  const [saving, setSaving] = useState(false);
  const isOverride = row.rate_source === 'override';

  useEffect(() => { setValue(String(row.rate)); }, [row.rate]);

  const save = async (newVal) => {
    setSaving(true);
    try {
      const num = newVal === null ? null : parseFloat(newVal);
      if (num !== null && (isNaN(num) || num < 0 || num > 500)) {
        toast.error('Rate must be between $0 and $500');
        setSaving(false);
        return;
      }
      const { data } = await axios.patch(
        `${API}/payroll/workers/${row.worker_id}/hourly-rate`,
        { hourly_rate: num },
        { headers: getAuth() }
      );
      onSaved(row.worker_id, data.hourly_rate);
      toast.success(num === null ? 'Reverted to role default' : 'Rate updated');
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" data-testid={`rate-edit-${row.worker_id}`}>
        <span className="text-slate-400 text-sm">$</span>
        <Input
          type="number"
          step="0.5"
          min="0"
          max="500"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save(value);
            if (e.key === 'Escape') { setEditing(false); setValue(String(row.rate)); }
          }}
          className="h-8 w-20 text-sm"
          autoFocus
          data-testid={`rate-input-${row.worker_id}`}
        />
        <span className="text-xs text-slate-400">/hr</span>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => save(value)} disabled={saving} data-testid={`rate-save-${row.worker_id}`}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 text-emerald-600" />}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 group" data-testid={`rate-display-${row.worker_id}`}>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-sm font-medium text-slate-700 hover:text-teal-700 underline decoration-dotted underline-offset-2"
        data-testid={`rate-edit-btn-${row.worker_id}`}
      >
        {money(row.rate)}/hr
      </button>
      {isOverride ? (
        <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0">override</Badge>
      ) : (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400">default</Badge>
      )}
      {isOverride && (
        <button
          type="button"
          onClick={() => save(null)}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          title="Revert to role default"
          data-testid={`rate-revert-${row.worker_id}`}
        >
          <RotateCcw className="h-3 w-3 text-slate-400 hover:text-slate-700" />
        </button>
      )}
    </div>
  );
}

export default function PayrollPage() {
  const navigate = useNavigate();
  const presets = useMemo(periodPresets, []);
  const [start, setStart] = useState(presets.thisFortnight.start);
  const [end, setEnd] = useState(presets.thisFortnight.end);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [payslipId, setPayslipId] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/payroll/summary`, {
        params: { start, end },
        headers: getAuth(),
      });
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payroll');
      if (e.response?.status === 403) navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [start, end, navigate]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const setPreset = (p) => { setStart(p.start); setEnd(p.end); };

  const handleRateSaved = (workerId, newRate) => {
    // Refresh so all pay figures recompute with the new rate
    fetchSummary();
    // Optimistically flag the row so the UI feels instant
    setSummary(s => s ? {
      ...s,
      rows: s.rows.map(r => r.worker_id === workerId
        ? { ...r, rate: newRate ?? r.rate, rate_source: newRate ? 'override' : 'default' }
        : r),
    } : s);
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/payroll/summary.csv`, {
        params: { start, end },
        headers: getAuth(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `payroll_${start}_to_${end}.csv`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch {
      toast.error('CSV export failed');
    } finally {
      setDownloading(false);
    }
  };

  const downloadPayslip = async (row) => {
    setPayslipId(row.worker_id);
    try {
      const resp = await axios.get(`${API}/payroll/payslip/${row.worker_id}.pdf`, {
        params: { start, end },
        headers: getAuth(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      const safe = (row.worker_name || 'worker').replace(/[^a-z0-9]+/gi, '_');
      a.href = url; a.download = `payslip_${safe}_${start}_to_${end}.pdf`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success(`Payslip downloaded for ${row.worker_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payslip failed');
    } finally {
      setPayslipId(null);
    }
  };

  const rows = summary?.rows || [];
  const totals = summary?.totals || { total_hours: 0, loading_hours: 0, gross_pay: 0, worker_count: 0, shift_count: 0 };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-teal-50/30">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin"><Button variant="ghost" size="sm" data-testid="payroll-back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button></Link>
            <LogoHome />
            <div className="hidden sm:block">
              <h1 className="font-heading text-lg font-semibold text-slate-900">Payroll</h1>
              <p className="text-xs text-slate-500">Gross pay based on clocked hours + SCHADS-style loadings</p>
            </div>
          </div>
          <Button
            onClick={downloadCsv}
            disabled={downloading || !rows.length}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="payroll-download-csv"
          >
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            CSV
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Period picker */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-wrap gap-2" data-testid="payroll-presets">
                {Object.entries(presets).map(([k, p]) => {
                  const active = start === p.start && end === p.end;
                  return (
                    <Button
                      key={k}
                      size="sm"
                      variant={active ? 'default' : 'outline'}
                      className={active ? 'bg-teal-600 hover:bg-teal-700' : ''}
                      onClick={() => setPreset(p)}
                      data-testid={`payroll-preset-${k}`}
                    >
                      {p.label}
                    </Button>
                  );
                })}
              </div>
              <div className="sm:ml-auto flex flex-wrap items-end gap-2 w-full sm:w-auto">
                <div className="flex-1 sm:flex-none min-w-0">
                  <label className="text-xs text-slate-500 block mb-1">From</label>
                  <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-full sm:w-40" data-testid="payroll-start" />
                </div>
                <div className="flex-1 sm:flex-none min-w-0">
                  <label className="text-xs text-slate-500 block mb-1">To</label>
                  <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-full sm:w-40" data-testid="payroll-end" />
                </div>
                <Button onClick={fetchSummary} variant="outline" className="h-9" data-testid="payroll-refresh">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarIcon className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card><CardContent className="p-5 flex items-center gap-3">
            <div className="rounded-full bg-teal-100 text-teal-700 p-3"><DollarSign className="h-5 w-5" /></div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Total gross</div>
              <div className="text-2xl font-heading font-semibold text-slate-900" data-testid="payroll-total-gross">{money(totals.gross_pay)}</div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-5 flex items-center gap-3">
            <div className="rounded-full bg-emerald-100 text-emerald-700 p-3"><Clock className="h-5 w-5" /></div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Total hours</div>
              <div className="text-2xl font-heading font-semibold text-slate-900" data-testid="payroll-total-hours">{Number(totals.total_hours).toFixed(2)}</div>
              <div className="text-xs text-amber-600 mt-0.5">
                {Number(totals.loading_hours).toFixed(2)} loaded (Sat/Sun/PH)
                {totals.night_hours > 0 && <span className="text-indigo-600"> · {Number(totals.night_hours).toFixed(2)} night</span>}
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-5 flex items-center gap-3">
            <div className="rounded-full bg-cyan-100 text-cyan-700 p-3"><Users2 className="h-5 w-5" /></div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Workers</div>
              <div className="text-2xl font-heading font-semibold text-slate-900" data-testid="payroll-worker-count">{totals.worker_count}</div>
              <div className="text-xs text-slate-500 mt-0.5">{totals.shift_count} shifts</div>
            </div>
          </CardContent></Card>
        </div>

        {/* Editable table */}
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-base flex items-center justify-between gap-2">
              <span>Worker breakdown</span>
              <span className="text-xs text-slate-400 font-normal">Click any rate to edit — updates apply instantly</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading && !summary ? (
              <div className="py-16 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
            ) : rows.length === 0 ? (
              <div className="py-16 text-center text-slate-500">No clocked shifts in this period.</div>
            ) : (
              <div className="overflow-x-auto" data-testid="payroll-table">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Worker</th>
                      <th className="text-right px-3 py-3">Base hrs</th>
                      <th className="text-right px-3 py-3">Sat hrs</th>
                      <th className="text-right px-3 py-3">Sun hrs</th>
                      <th className="text-right px-3 py-3">PH hrs</th>
                      <th className="text-right px-3 py-3">Base $</th>
                      <th className="text-right px-3 py-3">Loadings $</th>
                      <th className="text-right px-3 py-3" title="Sleepovers × flat allowance">Sleepover $</th>
                      <th className="text-right px-4 py-3 bg-teal-50/50">Gross $</th>
                      <th className="text-left px-4 py-3">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.worker_id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`payroll-row-${r.worker_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{r.worker_name}</div>
                          <div className="text-xs text-slate-500 capitalize">{r.role} · {r.shift_count} shift{r.shift_count === 1 ? '' : 's'}{r.manual_count > 0 && <span className="ml-1 normal-case text-[10px] font-semibold uppercase bg-teal-600 text-white px-1 rounded" title={`${r.manual_count} manually logged entr${r.manual_count === 1 ? 'y' : 'ies'} (approved)`} data-testid={`payroll-manual-${r.worker_id}`}>{r.manual_count} manual</span>}</div>
                        </td>
                        <td className="text-right px-3 py-3 text-slate-700">{r.base_hours.toFixed(2)}</td>
                        <td className="text-right px-3 py-3 text-slate-700">{r.saturday_hours > 0 ? <span className="text-amber-700 font-medium">{r.saturday_hours.toFixed(2)}</span> : '—'}</td>
                        <td className="text-right px-3 py-3 text-slate-700">{r.sunday_hours > 0 ? <span className="text-amber-700 font-medium">{r.sunday_hours.toFixed(2)}</span> : '—'}</td>
                        <td className="text-right px-3 py-3 text-slate-700">{r.public_holiday_hours > 0 ? <span className="text-red-700 font-medium">{r.public_holiday_hours.toFixed(2)}</span> : '—'}</td>
                        <td className="text-right px-3 py-3 text-slate-700">{money(r.base_pay)}</td>
                        <td className="text-right px-3 py-3 text-slate-700">{r.loading_pay > 0 ? <span className="text-emerald-700">+{money(r.loading_pay)}</span> : money(0)}</td>
                        <td className="text-right px-3 py-3 text-slate-700">
                          {r.sleepover_count > 0
                            ? <span className="text-purple-700 font-medium" title={`${r.sleepover_count} sleepover${r.sleepover_count === 1 ? '' : 's'}`}>+{money(r.sleepover_allowance)} <span className="text-[10px] text-slate-400">×{r.sleepover_count}</span></span>
                            : '—'}
                        </td>
                        <td className="text-right px-4 py-3 font-heading font-semibold text-slate-900 bg-teal-50/40" data-testid={`payroll-gross-${r.worker_id}`}>{money(r.gross_pay)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <EditableRateCell row={r} onSaved={handleRateSaved} />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              title="Download payslip PDF"
                              onClick={() => downloadPayslip(r)}
                              disabled={payslipId === r.worker_id}
                              data-testid={`payslip-btn-${r.worker_id}`}
                            >
                              {payslipId === r.worker_id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <FileText className="h-3.5 w-3.5 text-slate-500 hover:text-teal-700" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-semibold">
                    <tr className="border-t-2 border-slate-200">
                      <td className="px-4 py-3 text-slate-700">TOTAL</td>
                      <td className="text-right px-3 py-3">{rows.reduce((s, r) => s + r.base_hours, 0).toFixed(2)}</td>
                      <td className="text-right px-3 py-3">{rows.reduce((s, r) => s + r.saturday_hours, 0).toFixed(2)}</td>
                      <td className="text-right px-3 py-3">{rows.reduce((s, r) => s + r.sunday_hours, 0).toFixed(2)}</td>
                      <td className="text-right px-3 py-3">{rows.reduce((s, r) => s + r.public_holiday_hours, 0).toFixed(2)}</td>
                      <td className="text-right px-3 py-3">{money(rows.reduce((s, r) => s + r.base_pay, 0))}</td>
                      <td className="text-right px-3 py-3 text-emerald-700">+{money(rows.reduce((s, r) => s + r.loading_pay, 0))}</td>
                      <td className="text-right px-3 py-3 text-purple-700">{totals.sleepover_allowance > 0 ? `+${money(totals.sleepover_allowance)}` : money(0)}</td>
                      <td className="text-right px-4 py-3 text-teal-800 bg-teal-50">{money(totals.gross_pay)}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">—</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 text-center pb-8">
          Loadings: Sat +{summary?.config?.loading_saturday_pct ?? 50}% · Sun +{summary?.config?.loading_sunday_pct ?? 100}% · PH +{summary?.config?.loading_public_holiday_pct ?? 150}%
          {summary?.config?.loading_night_pct > 0 && (
            <> · Night +{summary.config.loading_night_pct}% (10pm–6am)</>
          )}
          {summary?.config?.sleepover_allowance_per_shift > 0 && (
            <> · Sleepover allowance {money(summary.config.sleepover_allowance_per_shift)} per shift</>
          )}.
          Gross pay only — import CSV into Xero / MYOB for tax + super.
        </p>
      </main>
    </div>
  );
}
